import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePostHog } from '@posthog/react';
import { STATUS_COLORS, STATUS_LABELS, getCompetitiveStyle, getFundingTypeStyle } from '../../utils/constants';
import { formatPaymentRates, getDeadlineDisplay } from '../../utils/helpers';
import SaveButton from './SaveButton';
import AlertButton from './AlertButton';
import Tooltip from '../common/Tooltip';
import AuthModal from '../auth/AuthModal';

const FundingRow = ({ funding, onUnsave, onCompatibilityFilter, onSaveToggle, compact = false }) => {
    const { isAuthenticated } = useAuth();
    const [expanded, setExpanded] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authAction, setAuthAction] = useState('');
    const navigate = useNavigate();
    const posthog = usePostHog();
    // Double-click detection
    const clickTimer = useRef(null);
    const DOUBLE_CLICK_DELAY = 250;

    const detailUrl = funding?.slug ? `/funding/${funding.slug}` : (funding?.id ? `/funding/${funding.id}` : '#');

    const handleViewDetails = () => {
        if (!isAuthenticated) {
            setAuthAction('view this funding opportunity');
            setShowAuthModal(true);
            return;
        }
        navigate(detailUrl);
    };

    const handleStretchedLinkClick = (e) => {
        // Regular left-click: prevent navigation, let the row div handle expand/collapse
        if (!e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            return;
        }
        // Cmd/Ctrl+Click or middle-click: navigate, but check auth first
        if (!isAuthenticated) {
            e.preventDefault();
            setAuthAction('view this funding opportunity');
            setShowAuthModal(true);
        }
    };

    const handleRowClick = (e) => {
        if (e?.metaKey || e?.ctrlKey) {
            navigate(detailUrl);
            return;
        }
        if (clickTimer.current) {
            // Second click within delay — double click
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
            handleViewDetails();
        } else {
            clickTimer.current = setTimeout(() => {
                clickTimer.current = null;
                if (!isAuthenticated) {
                    setAuthAction('see the details of this funding opportunity');
                    setShowAuthModal(true);
                    return;
                }
                const nextExpanded = !expanded;
                if (nextExpanded) {
                    posthog.capture('funding_row_expanded', {
                        funding_id: funding?.id,
                        funding_name: funding?.name,
                    });
                }
                setExpanded(nextExpanded);
            }, DOUBLE_CLICK_DELAY);
        }
    };

    const handleSaveToggle = (fundingId, isSaved) => {
        if (!isSaved && onUnsave) {
            onUnsave(fundingId);
        }
        if (onSaveToggle) onSaveToggle(fundingId, isSaved);
    };

    const formatCurrency = (funding) => formatPaymentRates(funding.payment_rates, { maxRates: 3 });

    const getStatusColor = (status) => {
        return STATUS_COLORS[status?.toLowerCase()] ? 'text-white' : 'bg-gray-100 text-gray-800';
    };

    const getStatusBg = (status) => {
        return STATUS_COLORS[status?.toLowerCase()] || 'transparent';
    };

    const getStatusLabel = (status) => {
        return STATUS_LABELS[status?.toLowerCase()] || status?.toUpperCase() || 'UNKNOWN';
    };

    const truncateDescription = (text, limit = 125) => {
        if (!text) return '';
        if (text.length <= limit) return text;
        return text.substring(0, limit).trim() + '...';
    };

    const parseArrayString = (data) => {
        if (!data) return [];
        let result = data;
        if (typeof result === 'string') {
            if (result.startsWith('[')) {
                try {
                    const jsonString = result.replace(/'/g, '"');
                    result = JSON.parse(jsonString);
                } catch (e) {
                    result = [result];
                }
            } else {
                result = [result];
            }
        } else if (!Array.isArray(result)) {
            result = [result];
        }
        return result.filter(item => item && item.toString().trim());
    };

    const fundingData = funding || {};
    const funders = fundingData.funders || [];

    return (
        <div
            className="relative bg-white border border-gray-100 hover:border-gray-200 transition-all duration-200"
            style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
        >
            {/* Stretched link — enables middle-click and right-click "open in new tab" on the row */}
            <a
                href={detailUrl}
                onClick={handleStretchedLinkClick}
                className="absolute inset-0 z-0"
                aria-label={`View details for ${fundingData.name}`}
            />

            {/* Collapsed Row — elevated above the stretched link */}
            <div
                className="relative z-10 flex items-start px-3 py-2.5 cursor-pointer gap-0"
                onClick={handleRowClick}
                title="Click to expand · Double-click for full details"
            >
                {/* Expand/Collapse Chevron */}
                <svg
                    className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200 mr-2.5 mt-1 ${expanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>

                {/*
                  Cream pill: inline-flex so it hugs its content.
                  Grows up to maxWidth, then the name truncates.
                  Status sits in a fixed-width slot (wide enough for
                  "Invitation Required") so it's always centred consistently.
                  In compact mode, status hugs content instead.
                */}
                <div
                    className="flex items-start gap-2 min-w-0 py-0.5"
                    style={{
                        maxWidth: compact ? 'none' : '640px',
                        minWidth: compact ? 0 : '120px',
                        flexShrink: 1,
                    }}
                >
                    {/* Status dot — aligned to first line of text */}
                    <div
                        className="flex-shrink-0 rounded-full mt-1"
                        style={{
                            width: 10,
                            height: 10,
                            backgroundColor: getStatusBg(fundingData.status) || '#9ca3af',
                        }}
                        title={getStatusLabel(fundingData.status)}
                    />

                    {/* Name — wraps onto multiple lines, row grows taller */}
                    <span
                        className="text-sm font-semibold text-left break-words"
                        style={{ color: '#222222', minWidth: 0 }}
                    >
                        {fundingData.name || fundingData.description}
                    </span>
                </div>

                {/* Spacer pushes funder + payment to the right */}
                <div className="flex-1" />

                {/* Funder badges — right-aligned, hidden on very small screens and in compact mode */}
                {!compact && funders.length > 0 && (
                    <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0 justify-end" style={{ maxWidth: '220px' }}>
                        {funders.slice(0, 2).map((funder, index) => (
                            <span
                                key={`funder-${index}`}
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium text-white truncate"
                                style={{ backgroundColor: '#615DEC', maxWidth: '160px' }}
                                title={funder}
                            >
                                {funder}
                            </span>
                        ))}
                    </div>
                )}

                {/* Status pill — tablet/desktop only, next to funder badges */}
                {!compact && (
                    <span
                        className={`hidden sm:inline-flex flex-shrink-0 items-center ml-1.5 px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap ${getStatusColor(fundingData.status)}`}
                        style={{ backgroundColor: getStatusBg(fundingData.status) }}
                    >
                        {getStatusLabel(fundingData.status)}
                    </span>
                )}

                {/* Subtle vertical divider */}
                {!compact && (
                    <div
                        className="hidden sm:block flex-shrink-0 mx-3 self-stretch"
                        style={{ width: '1px', backgroundColor: '#E5E5E5' }}
                    />
                )}

                {/*
                  Payment value — fixed width to comfortably hold the longest
                  observed value: "£363/visit; £9,075/yr max" (~22 chars).
                  Text is centred within that reserved space.
                */}
                <div
                    className="hidden sm:flex flex-shrink-0 items-center justify-center"
                    style={{ width: '172px' }}
                >
                    <span
                        className="text-sm font-semibold text-center leading-tight"
                        style={{ color: '#615DEC' }}
                    >
                        {formatCurrency(fundingData)}
                    </span>
                </div>

                {/* Subtle vertical divider */}
                <div
                    className="flex-shrink-0 mx-2 self-stretch"
                    style={{ width: '1px', backgroundColor: '#E5E5E5' }}
                />

                {/* Compatibility + Save Buttons */}
                <div
                    className="flex-shrink-0 flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                >
                    {!compact && (
                        <Tooltip text="Add to stack">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    posthog.capture('compatible_schemes_searched', {
                                        funding_id: fundingData.id,
                                        funding_name: fundingData.name,
                                    });
                                    if (onCompatibilityFilter) {
                                        onCompatibilityFilter({ id: fundingData.id, name: fundingData.name, fund_collection: fundingData.fund_collection, payment_rates: fundingData.payment_rates });
                                    } else {
                                        navigate(`/funding?compatible_with=${fundingData.id}&compatible_name=${encodeURIComponent(fundingData.name || '')}`);
                                    }
                                }}
                                className="p-1.5 text-gray-400 hover:text-gray-700 transition-colors"
                            >
                                <svg className="h-5 w-5 translate-y-[1px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                                </svg>
                            </button>
                        </Tooltip>
                    )}
                    <AlertButton schemeId={fundingData.id} />
                    <SaveButton
                        funding={fundingData}
                        onSaveToggle={handleSaveToggle}
                    />
                </div>
            </div>

            {/* Expanded Content */}
            {expanded && (
                <div className="relative z-10 border-t border-gray-100 px-4 py-4 ml-7">
                    {/* Full name — visible when the header row truncated it */}
                    {fundingData.name && (
                        <p className="text-sm font-semibold mb-3" style={{ color: '#222222', lineHeight: 1.4 }}>
                            {fundingData.name}
                        </p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                        {/* Left Column */}
                        <div className="space-y-3">
                            {/* Description */}
                            {fundingData.description && (
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#7C7B7B' }}>
                                        Description
                                    </p>
                                    <p className="text-sm leading-relaxed" style={{ color: '#222222' }}>
                                        {truncateDescription(fundingData.description, 300)}
                                    </p>
                                </div>
                            )}

                            {/* Funder (visible on mobile where hidden in row) */}
                            {funders.length > 0 && (
                                <div className="sm:hidden">
                                    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#7C7B7B' }}>
                                        Funder
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {funders.map((funder, index) => (
                                            <span
                                                key={`exp-funder-${index}`}
                                                className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium text-white"
                                                style={{ backgroundColor: '#615DEC' }}
                                            >
                                                {funder}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Fund Collection */}
                            {fundingData.fund_collection && (
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#7C7B7B' }}>
                                        Fund Collection
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {parseArrayString(fundingData.fund_collection).map((collection, index) => (
                                            <span
                                                key={`collection-${index}`}
                                                className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-800"
                                            >
                                                {collection}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Farm Types */}
                            {fundingData.farm_types && fundingData.farm_types.length > 0 && (
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#7C7B7B' }}>
                                        Farm Types
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {fundingData.farm_types.map((type, index) => (
                                            <span
                                                key={index}
                                                className="px-2.5 py-0.5 text-xs font-semibold rounded-full"
                                                style={{ backgroundColor: '#F6F5EC', color: '#222222' }}
                                            >
                                                {type}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right Column - Key Details */}
                        <div className="space-y-2.5">
                            {/* Payment rate — mobile only (hidden in collapsed row on small screens) */}
                            {formatCurrency(fundingData) && (
                                <div className="sm:hidden">
                                    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#7C7B7B' }}>
                                        Payment Rate
                                    </p>
                                    <p className="text-sm font-semibold leading-tight" style={{ color: '#615DEC' }}>
                                        {formatCurrency(fundingData)}
                                    </p>
                                </div>
                            )}

                            {/* Supplementary */}
                            {fundingData.is_supplementary && (
                                <div className="flex flex-wrap gap-1.5">
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center',
                                        padding: '3px 10px', borderRadius: 20,
                                        fontSize: '0.7rem', fontWeight: 600,
                                        color: '#615DEC', border: '1.5px solid #615DEC',
                                        background: 'transparent',
                                    }}>
                                        Supplementary
                                    </span>
                                </div>
                            )}

                            {/* Competitive & Funding Type */}
                            {(fundingData.competitive?.filter(Boolean).length > 0 || fundingData.funding_type) && (
                                <div className="flex flex-wrap gap-1.5">
                                    {fundingData.competitive?.filter(Boolean).map((c, i) => {
                                        const cs = getCompetitiveStyle(c);
                                        return (
                                            <span key={i} style={{
                                                display: 'inline-flex', alignItems: 'center',
                                                padding: '3px 10px', borderRadius: 20,
                                                fontSize: '0.7rem', fontWeight: 600,
                                                background: cs.bg, color: cs.color,
                                            }}>
                                                {cs.label}
                                            </span>
                                        );
                                    })}
                                    {fundingData.funding_type && (() => {
                                        const fts = getFundingTypeStyle(fundingData.funding_type);
                                        return (
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center',
                                                padding: '3px 10px', borderRadius: 20,
                                                fontSize: '0.7rem', fontWeight: 600,
                                                background: fts.bg, color: fts.color,
                                            }}>
                                                {fts.label}
                                            </span>
                                        );
                                    })()}
                                </div>
                            )}
                            {/* Duration */}
                            {fundingData.duration && (
                                <div className="flex items-center text-sm" style={{ color: '#7C7B7B' }}>
                                    <svg className="h-4 w-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="font-medium">Duration:</span>
                                    <span className="ml-2">{fundingData.duration}</span>
                                </div>
                            )}

                            {/* Deadline */}
                            {(fundingData.deadline_date || fundingData.deadline_tag) && (
                                <div className="flex items-center text-sm" style={{ color: '#7C7B7B' }}>
                                    <svg className="h-4 w-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="font-medium">Deadline:</span>
                                    <span className="ml-2">{getDeadlineDisplay(fundingData)}</span>
                                </div>
                            )}

                            {/* Location */}
                            {/* View Details Link */}
                            <div className="pt-2">
                                <a
                                    href={detailUrl}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isAuthenticated) {
                                            e.preventDefault();
                                            setAuthAction('view this funding opportunity');
                                            setShowAuthModal(true);
                                        }
                                    }}
                                    className="text-sm font-semibold hover:underline transition-all"
                                    style={{ color: '#615DEC' }}
                                >
                                    View full details →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* AuthModal for unauthenticated users */}
            <AuthModal
                isOpen={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                action={authAction}
                fundingId={funding.slug || funding.id}
            />
        </div>
    );
};

export default FundingRow;